import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { App, Input, Modal, Radio, Select } from 'antd'
import BrandButton from '@/components/BrandButton'
import {
  SettlementApiCode,
  SettlementApiError,
  WALLET_CHAINS,
  WALLET_TYPES,
  WalletType,
  getAvailableBalance,
  withdraw,
  type WalletChain,
} from '@/api/settlements'
import {
  CHAIN_I18N,
  WALLET_I18N,
  WITHDRAW_I18N,
  WithdrawError,
  acceptAmount,
  allBalanceInput,
  formatCny,
  hasWithdrawErrors,
  sanitizeAddress,
  validateWithdraw,
} from './format'

function SettlementsWithdrawModal({
  open,
  balance,
  onClose,
  onDone,
}: {
  open: boolean
  balance: number
  onClose: () => void
  onDone: (next: number) => void
}) {
  const { message } = App.useApp()
  const { t } = useTranslation()
  const [wallet, setWallet] = useState<WalletType>(WalletType.Hibro)
  const [amount, setAmount] = useState('')
  const [chain, setChain] = useState<WalletChain | undefined>()
  const [address, setAddress] = useState('')
  const [amountErr, setAmountErr] = useState('')
  const [chainErr, setChainErr] = useState('')
  const [addressErr, setAddressErr] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [cap, setCap] = useState(balance)

  useEffect(() => {
    if (!open) return
    setWallet(WalletType.Hibro)
    setAmount('')
    setChain(undefined)
    setAddress('')
    setAmountErr('')
    setChainErr('')
    setAddressErr('')
    setCap(balance)
  }, [open, balance])

  const showErrors = (next: ReturnType<typeof validateWithdraw>) => {
    setAmountErr(next.amount ? t(WITHDRAW_I18N[next.amount]) : '')
    setChainErr(next.chain ? t(WITHDRAW_I18N[next.chain]) : '')
    setAddressErr(next.address ? t(WITHDRAW_I18N[next.address]) : '')
  }

  const submit = async () => {
    const next = validateWithdraw({ amount, cap, wallet, chain, address })
    showErrors(next)
    if (hasWithdrawErrors(next)) return

    setSubmitting(true)
    try {
      const latest = await getAvailableBalance()
      setCap(latest)
      if (Number(amount) > latest) {
        setAmountErr(t(WITHDRAW_I18N[WithdrawError.OverBalance]))
        return
      }
      const res = await withdraw({
        wallet,
        amount: Number(amount),
        chain: wallet === WalletType.External ? chain : undefined,
        address: wallet === WalletType.External ? address : undefined,
      })
      message.success(
        wallet === WalletType.Hibro ? t('settlements.withdrawOkHibro') : t('settlements.withdrawOkExt'),
      )
      onDone(res.balance)
      onClose()
    } catch (err) {
      if (err instanceof SettlementApiError && err.code === SettlementApiCode.Insufficient) {
        message.error(t('settlements.insufficient'))
        void getAvailableBalance().then(setCap)
      } else {
        message.error(t('settlements.withdrawFail'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title={t('settlements.withdraw')}
      open={open}
      getContainer={() => document.body}
      onCancel={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <BrandButton variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </BrandButton>
          <BrandButton
            variant={wallet === WalletType.External ? 'danger' : 'primary'}
            loading={submitting}
            onClick={() => void submit()}
          >
            {t('settlements.confirmWithdraw')}
          </BrandButton>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <div className="mb-2">{t('settlements.wallet')}</div>
          <Radio.Group
            value={wallet}
            onChange={(e) => {
              const next = e.target.value as WalletType
              setWallet(next)
              if (next === WalletType.Hibro) {
                setChain(undefined)
                setAddress('')
                setChainErr('')
                setAddressErr('')
              }
            }}
          >
            {WALLET_TYPES.map((type) => (
              <Radio key={type} value={type}>
                {t(WALLET_I18N[type])}
              </Radio>
            ))}
          </Radio.Group>
        </div>
        <div>
          <div className="mb-2">{t('settlements.amountLabel')}</div>
          <div className="flex gap-2">
            <Input
              placeholder={t('settlements.amountPh')}
              value={amount}
              status={amountErr ? 'error' : undefined}
              onChange={(e) => {
                if (acceptAmount(e.target.value)) setAmount(e.target.value)
                setAmountErr('')
              }}
            />
            <BrandButton variant="outline" onClick={() => setAmount(allBalanceInput(cap))}>
              {t('settlements.allBalance')}
            </BrandButton>
          </div>
          {amountErr && <div className="mt-1 text-xs text-red-500">{amountErr}</div>}
          <div className="mt-1 text-xs text-muted">
            {t('settlements.available')} {formatCny(cap, true)}
          </div>
        </div>
        {wallet === WalletType.External && (
          <>
            <div>
              <div className="mb-2">{t('settlements.chain')}</div>
              <Select
                className="w-full"
                placeholder={t('settlements.chainPh')}
                status={chainErr ? 'error' : undefined}
                value={chain}
                onChange={(v) => {
                  setChain(v)
                  setChainErr('')
                }}
                options={WALLET_CHAINS.map((item) => ({
                  value: item,
                  label: t(CHAIN_I18N[item]),
                }))}
              />
              {chainErr && <div className="mt-1 text-xs text-red-500">{chainErr}</div>}
            </div>
            <div>
              <div className="mb-2">{t('settlements.address')}</div>
              <Input
                placeholder={t('settlements.addressPh')}
                status={addressErr ? 'error' : undefined}
                value={address}
                maxLength={128}
                onChange={(e) => {
                  setAddress(sanitizeAddress(e.target.value))
                  setAddressErr('')
                }}
              />
              {addressErr && <div className="mt-1 text-xs text-red-500">{addressErr}</div>}
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

export default SettlementsWithdrawModal
